import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../common/Icon/Icon';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import { adminFetch, ago, shortDate } from './adminUtil';

/**
 * Open reports, one card per reported trader with what people said,
 * and the two ways to close them: dismiss (nothing wrong) or ban.
 * Ban blocks login and hides them everywhere, deletes nothing, and can
 * be undone from Look up.
 */
function AdminReports({ onChanged, go }) {
    const [data, setData] = useState(null);
    const [filings, setFilings] = useState({});
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [banFor, setBanFor] = useState(null);
    const [banReason, setBanReason] = useState('');

    const load = useCallback(async () => {
        try {
            const d = await adminFetch('/api/admin/reports');
            setData(d);
            // Show what people wrote without a click: fetch each trader's filings
            const entries = await Promise.all(d.groups.slice(0, 30).map(g =>
                adminFetch(`/api/admin/reports/${g.userId}`).then(r => [g.userId, r.reports || []]).catch(() => [g.userId, []])));
            setFilings(Object.fromEntries(entries));
        } catch (e) { setMsg(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const act = async (url, body, done) => {
        setBusy(true); setMsg(null);
        try {
            await adminFetch(url, { method: 'POST', body: body || {} });
            setMsg(done);
            await load();
            onChanged();
        } catch (e) { setMsg(e.message); }
        finally { setBusy(false); setBanFor(null); }
    };

    if (!data) return <p className="adm-loading">Loading reports&hellip;</p>;

    return (
        <div className="adm-reports">
            <ConfirmDialog
                open={!!banFor}
                title={`Ban ${banFor?.username}?`}
                confirmLabel="Ban account"
                danger
                busy={busy}
                onCancel={() => setBanFor(null)}
                onConfirm={() => act(`/api/admin/users/${banFor.userId}/ban`, { reason: banReason.trim() }, `${banFor.username} is banned.`)}
                message={(
                    <div className="adm-confirm-body">
                        <p>They're signed out, can't log in, and disappear from search, matches and trades. Nothing is deleted and you can unban later from Look up. Their open reports resolve.</p>
                        <textarea rows={2} maxLength={255} placeholder="Reason (kept on record, optional)"
                            value={banReason} onChange={e => setBanReason(e.target.value)} />
                    </div>
                )}
            />
            {msg && <p className="adm-msg">{msg}</p>}

            {data.groups.length === 0 ? (
                <div className="adm-empty">
                    <Icon name="check-circle" size={28} />
                    No open reports. All clear.
                </div>
            ) : (
                <ul className="adm-report-cards">
                    {data.groups.map(g => (
                        <li key={g.userId} className="adm-report-card">
                            <header>
                                <button type="button" className="adm-party" onClick={() => go('lookup', { user: g.userId })}>
                                    <AvatarFrame user={g} className="adm-avatar">
                                        <img src={getProfilePhotoSrc(g)} alt="" width={40} height={40}
                                            onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }} />
                                    </AvatarFrame>
                                    <span>
                                        <strong>{g.username}</strong>
                                        <small>{g.openCount} open report{g.openCount === 1 ? '' : 's'} · latest {ago(g.latest)}</small>
                                    </span>
                                </button>
                                <div className="adm-chips">
                                    {g.is_banned && <span className="adm-chip bad">banned</span>}
                                    {g.reasons.map(r => <span key={r} className="adm-chip warn">{r}</span>)}
                                </div>
                            </header>
                            <ul className="adm-filings">
                                {(filings[g.userId] || []).filter(f => f.status === 'open').map(f => (
                                    <li key={f.id}>
                                        <span><strong>{f.reporter_username}</strong> · {f.reason} · {shortDate(f.created_at)}</span>
                                        {f.detail ? <p>{f.detail}</p> : <p className="adm-muted">No details given.</p>}
                                    </li>
                                ))}
                            </ul>
                            <div className="adm-actions">
                                <button type="button" className="adm-btn" disabled={busy}
                                    onClick={() => act(`/api/admin/reports/${g.userId}/dismiss`, null, `Reports on ${g.username} dismissed.`)}>
                                    <Icon name="check" size={13} /> Dismiss, nothing wrong
                                </button>
                                {g.is_banned ? (
                                    <button type="button" className="adm-btn" disabled={busy}
                                        onClick={() => act(`/api/admin/users/${g.userId}/unban`, null, `${g.username} is unbanned.`)}>
                                        Unban
                                    </button>
                                ) : (
                                    <button type="button" className="adm-btn danger" disabled={busy}
                                        onClick={() => { setBanReason(''); setBanFor(g); }}>
                                        <Icon name="block" size={13} /> Ban
                                    </button>
                                )}
                                <button type="button" className="adm-btn ghost" onClick={() => go('lookup', { user: g.userId })}>
                                    Full record <Icon name="arrow-right" size={13} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default AdminReports;
